import { cva } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from 'react';

import { useProfilesData, useSelectVariant } from '@/features/user';
import { useNotifications } from '@/stores/notifications';
import { cn } from '@/utils';

const variantDropdownVariants = cva(
  'flex w-full items-center justify-between rounded-3xl border-2 border-solid px-2 lg:px-6 py-3 text-sm font-medium transition-all duration-200 cursor-pointer select-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-white border-magical-moonlight-d1 text-magical-moonlight-dark hover:border-magical-moonlight-dark focus-visible:border-magical-moonlight-dark',
        open: 'bg-white border-magical-moonlight-dark text-magical-moonlight-dark',
        mobile:
          'bg-magical-moonlight-d2 border-magical-moonlight-d1 text-white hover:bg-magical-moonlight-d1 focus-visible:border-white',
        mobileOpen: 'bg-magical-moonlight-d1 border-white text-white',
      },
      size: {
        default: 'h-full text-sm',
        sm: 'h-full text-xs px-2 py-1 rounded-2xl max-w-24',
        lg: 'h-full text-base px-8 py-4',
        full: 'h-full text-sm',
        mobile: 'h-auto text-sm px-4 py-2.5 rounded-2xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const optionVariants = cva(
  'px-6 py-3 cursor-pointer text-sm transition-colors duration-150 hover:bg-magical-moonlight-s/50',
  {
    variants: {
      selected: {
        true: 'bg-magical-moonlight-s text-magical-moonlight-dark font-bold',
        false: 'text-magical-moonlight-dark',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed hover:bg-transparent',
        false: '',
      },
    },
    defaultVariants: {
      selected: false,
      disabled: false,
    },
  }
);

/**
 * A course arrives without a usable name when its LMSCourse row is missing or
 * archived — `service-user-oc` only sets `course_name` when that lookup hits.
 * Label those rather than hiding them: `select_variant` validates the
 * ProductVariants row alone, so the backend still accepts selecting them.
 * Checked explicitly (not `??`) because a blank name arrives as '' , which is
 * not nullish and would otherwise render an empty row.
 */
const courseLabel = (course: { course_name?: string }): string => {
  const name = course.course_name?.trim();
  return name === undefined || name === '' ? 'Course' : name;
};

/** Trigger label: the selected course's name, or the placeholder when none. */
const selectedCourseLabel = (
  course?: { course_name?: string } | null
): string => (course ? courseLabel(course) : 'Select course');

export interface VariantDropdownProps {
  className?: string;
  size?: 'sm' | 'default' | 'lg' | 'full' | 'mobile';
  variant?: 'default' | 'mobile';
}

const VariantDropdown = forwardRef<HTMLDivElement, VariantDropdownProps>(
  (
    { className, size = 'default', variant: variantProp = 'default', ...props },
    ref
  ) => {
    const { addNotification } = useNotifications();
    const { enrolledCourses, selectedCourse } = useProfilesData();

    // Every enrolled course is selectable; nameless ones get a fallback label
    // via courseLabel. Filtering them out instead made this list disagree with
    // the headers' `enrolledCourses.length > 1` mount gate, which left the menu
    // openable but empty and let the auto-select effect below switch courses.
    const selectableCourses = useMemo(
      () => enrolledCourses ?? [],
      [enrolledCourses]
    );
    const { mutate: selectVariant } = useSelectVariant({
      onSuccess: () => {
        addNotification({
          title: 'Course selected successfully',
          type: 'success',
        });
      },
      onError: () => {
        addNotification({
          title: 'Failed to select course',
          type: 'error',
        });
      },
    });
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const generatedId = useId();
    const dropdownId = generatedId;

    const handleToggle = useCallback(() => {
      setIsOpen(!isOpen);
    }, [isOpen]);

    const handleOptionSelect = useCallback(
      (variantId: string) => {
        selectVariant(variantId);
        setIsOpen(false);
      },
      [selectVariant]
    );

    const handleClickOutside = useCallback((event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }, []);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        } else if (e.key === 'Escape') {
          setIsOpen(false);
        }
      },
      [handleToggle]
    );

    // Auto-select when only one course is selectable. `selectVariant` invalidates
    // every query on success, which hands us a fresh `enrolledCourses` array and
    // so a fresh `selectableCourses` identity — re-running this effect. Without
    // the "already selected" check below that re-fires the mutation, which
    // invalidates again, and the dropdown selects the same variant in a loop.
    useEffect(() => {
      const [onlyCourse] = selectableCourses;
      if (
        selectableCourses.length === 1 &&
        onlyCourse.variant_id !== selectedCourse?.variant_id
      ) {
        handleOptionSelect(onlyCourse.variant_id);
        setIsOpen(false);
      }
    }, [selectableCourses, selectedCourse?.variant_id, handleOptionSelect]);

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [handleClickOutside]);

    const isMobile = variantProp === 'mobile';

    let dropdownVariant: 'default' | 'open' | 'mobile' | 'mobileOpen';
    if (isMobile) {
      dropdownVariant = isOpen ? 'mobileOpen' : 'mobile';
    } else {
      dropdownVariant = isOpen ? 'open' : 'default';
    }

    return (
      <div className="relative w-full h-full" ref={dropdownRef}>
        <div
          ref={ref}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${dropdownId}-listbox`}
          tabIndex={0}
          className={cn(
            variantDropdownVariants({
              variant: dropdownVariant,
              size,
            }),
            className
          )}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          {...props}
        >
          <span
            className={cn(
              'text-left text-sm font-bold line-clamp-2',
              isMobile && 'text-white',
              !isMobile && selectedCourse && 'text-magical-moonlight-dark',
              !isMobile &&
                !selectedCourse &&
                'text-magical-moonlight-d1 text-sm'
            )}
          >
            {selectedCourseLabel(selectedCourse)}
          </span>

          <ChevronDown
            className={cn(
              'h-5 w-5 transition-transform duration-200 ml-2',
              isMobile ? 'text-white' : 'text-magical-moonlight-dark',
              isOpen && 'rotate-180'
            )}
          />
        </div>

        {/* Dropdown menu */}
        {isOpen && selectableCourses.length > 0 ? (
          <div
            id={`${dropdownId}-listbox`}
            role="listbox"
            className={cn(
              'absolute top-full left-0 right-0 z-50 mt-1 border-2 rounded-2xl shadow-lg max-h-60 overflow-y-auto',
              isMobile
                ? 'bg-magical-moonlight-d2 border-magical-moonlight-d1'
                : 'bg-white border-magical-moonlight-d1'
            )}
          >
            {selectableCourses.map(course => {
              const isSelected =
                selectedCourse?.variant_id === course.variant_id;

              let optionClassName = '';
              if (isMobile) {
                const mobileSelectedClass = isSelected
                  ? 'bg-magical-moonlight-d1 text-white font-bold'
                  : 'text-white hover:bg-magical-moonlight-d1/50';
                optionClassName = cn(
                  'px-6 py-3 cursor-pointer text-sm transition-colors duration-150',
                  mobileSelectedClass
                );
              } else {
                optionClassName = cn(
                  'px-6 py-3 cursor-pointer text-sm transition-colors duration-150',
                  optionVariants({
                    selected: isSelected,
                    disabled: false,
                  })
                );
              }

              return (
                <div
                  key={course.variant_id}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  className={optionClassName}
                  onClick={() => handleOptionSelect(course.variant_id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOptionSelect(course.variant_id);
                    }
                  }}
                >
                  {courseLabel(course)}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }
);

VariantDropdown.displayName = 'VariantDropdown';

export { VariantDropdown };
export default VariantDropdown;
